import React from "react";
import { Link } from "react-router-dom";
import {
  SCROLL_OFFSET,
  ARTICLE_COLUMN,
  H2,
  BODY,
  BODY_SM,
  STACK_GAP,
  INLINE_LINK,
  SOFT_PANEL,
} from "./sections";

const FIT_CARDS = [
  {
    title: "When Built-In Dictation Is Enough",
    intro: "Built-in dictation is a reasonable choice if:",
    points: [
      "You dictate short, simple messages occasionally",
      "You need basic accessibility support for hands-free typing",
      "You don't need the output to match a specific tone or format",
      "You're comfortable doing a manual edit pass every time",
    ],
  },
  {
    title: "When You Need AI Voice Typing Instead",
    intro: "An AI voice typing tool makes more sense if:",
    points: [
      "You write across multiple apps daily (email, docs, chat, AI tools)",
      "You use consistent names, terms, or jargon that generic dictation keeps misspelling",
      "You want dictated text to come out publish-ready, not just transcribed",
      "You're dictating regularly enough that a few minutes of cleanup per message adds up to hours per week",
    ],
  },
];

const EditingGapSection = () => {
  return (
    <section
      id="accuracy-speed-editing-gap"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className={ARTICLE_COLUMN}
    >
      <h2 className={H2}>Accuracy, Speed, and the Editing Gap</h2>

      <p className={`${STACK_GAP} ${BODY}`}>
        The accuracy numbers on built-in dictation tools have genuinely
        improved over the years. That&apos;s not really where the gap is
        anymore. The gap is in what happens after the words land on the page.
      </p>

      <p className={`${STACK_GAP} ${BODY}`}>
        Built-in dictation stops at transcription. It doesn&apos;t know if you
        wanted a bullet list, a formal email, or a casual Slack reply. Every
        dictation session still ends with a manual editing pass: fixing
        &quot;um&quot;s and false starts, adding punctuation the tool missed,
        reformatting run-on sentences into something readable.
      </p>

      <p className={`${STACK_GAP} ${BODY}`}>
        AI voice typing tools close that gap by applying rewriting on top of
        transcription. Zero Typing claims roughly 5x faster efficiency versus
        typing manually, largely because that editing pass is handled
        automatically rather than left to you. That&apos;s the actual time
        savings people notice: not faster transcription, but skipping the
        cleanup entirely.
      </p>

      {/*
        "Which one fits your workflow" - the two decision cards. They carry
        their own id so the sidebar can jump straight to them even though they
        sit under this section's heading.
      */}
      <div
        id="which-one-fits-your-workflow"
        style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
        className="pt-[30.05px]"
      >
        <div className="grid grid-cols-1 gap-[17.17px] md:grid-cols-2 items-stretch">
          {FIT_CARDS.map(({ title, intro, points }) => (
            <article
              key={title}
              className={`flex flex-col ${SOFT_PANEL} p-[20px] sm:p-[25.76px]`}
            >
              <h3 className="font-jakarta text-[18px] font-bold leading-[28px] tracking-[-0.2px] text-[#0A0D16]">
                {title}
              </h3>

              <p className={`mt-[12.88px] ${BODY_SM}`}>{intro}</p>

              <ul className="mt-[12.88px] flex flex-col gap-[8px] pl-[18px] list-disc marker:text-[#0A0D1699]">
                {points.map((point) => (
                  <li key={point} className={`pl-[2px] ${BODY_SM}`}>
                    {point}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>

      <p className={`mt-[30.05px] ${BODY}`}>
        If that sounds like your day, it&apos;s worth reading{" "}
        <Link to="/how-ai-voice-typing-works" className={INLINE_LINK}>
          how AI voice typing actually works
        </Link>{" "}
        under the hood to see what&apos;s happening beyond simple
        transcription.
      </p>
    </section>
  );
};

export default EditingGapSection;
